import React, { useEffect, useRef, useState } from 'react';
import { Card, Btn, Input, Select, C } from './UI';
import { AddressAutocomplete } from './AddressAutocomplete';
import { api } from '../services/api';

declare global {
    interface Window { google: any; }
}

type Mode = 'county' | 'radius';
type RuleMode = 'Include' | 'Exclude';

interface CountyResult {
    state: string;
    county: string;
    bounds: any;
}

// Map-assisted service area rule creation: search a county and see an approximate
// bounding-box preview, or search an address + radius and see a live circle preview.
// Coverage enforcement itself is unaffected by this UI — it just makes the existing
// County-rule flow easier to use accurately, and is the only way to create a Radius rule.
export function ServiceAreaMapPanel({ onRuleAdded }: { onRuleAdded: () => void }) {
    const mapRef = useRef<HTMLDivElement>(null);
    const googleMapRef = useRef<any>(null);
    const rectangleRef = useRef<any>(null);
    const circleRef = useRef<any>(null);
    const [ready, setReady] = useState(false);

    const [mode, setMode] = useState<Mode>('county');
    const [ruleMode, setRuleMode] = useState<RuleMode>('Include');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    // County mode state
    const [countyQuery, setCountyQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [countyResult, setCountyResult] = useState<CountyResult | null>(null);

    // Radius mode state
    const [radiusAddress, setRadiusAddress] = useState('');
    const [radiusLat, setRadiusLat] = useState<number | null>(null);
    const [radiusLng, setRadiusLng] = useState<number | null>(null);
    const [radiusMiles, setRadiusMiles] = useState('25');

    useEffect(() => {
        const check = () => {
            if (window.google?.maps) setReady(true);
            else setTimeout(check, 200);
        };
        check();
    }, []);

    useEffect(() => {
        if (!ready || !mapRef.current || googleMapRef.current) return;
        googleMapRef.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 34.7304, lng: -86.5861 },
            zoom: 7,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
        });
    }, [ready]);

    const clearOverlays = () => {
        rectangleRef.current?.setMap(null);
        rectangleRef.current = null;
        circleRef.current?.setMap(null);
        circleRef.current = null;
    };

    const switchMode = (m: Mode) => {
        setMode(m);
        setError('');
        clearOverlays();
        setCountyResult(null);
    };

    const searchCounty = () => {
        if (!countyQuery.trim() || !window.google?.maps) return;
        setError('');
        setSearching(true);
        clearOverlays();
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: `${countyQuery.trim()}, USA` }, (results: any[], status: string) => {
            setSearching(false);
            if (status !== 'OK' || !results?.length) {
                setError('Could not find that county — try "County Name, ST".');
                return;
            }
            const result = results[0];
            const find = (type: string) => result.address_components.find((c: any) => c.types.includes(type));
            const county = find('administrative_area_level_2')?.long_name;
            const state = find('administrative_area_level_1')?.short_name;
            if (!county || !state) {
                setError('That search didn\'t resolve to a county — try "County Name, ST".');
                return;
            }
            const bounds = result.geometry.viewport;
            rectangleRef.current = new window.google.maps.Rectangle({
                bounds, map: googleMapRef.current,
                fillColor: '#1D9E75', fillOpacity: 0.15, strokeColor: '#1D9E75', strokeWeight: 2,
            });
            googleMapRef.current.fitBounds(bounds);
            setCountyResult({ state, county, bounds });
        });
    };

    const confirmCounty = async () => {
        if (!countyResult) return;
        setSaving(true);
        setError('');
        try {
            await api.addServiceAreaRule('County', ruleMode, countyResult.state, countyResult.county);
            resetCounty();
            onRuleAdded();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const resetCounty = () => {
        setCountyQuery('');
        setCountyResult(null);
        clearOverlays();
    };

    // Live-update the radius circle whenever the address or radius value changes
    useEffect(() => {
        if (mode !== 'radius' || radiusLat == null || radiusLng == null || !googleMapRef.current) return;
        const miles = parseFloat(radiusMiles);
        if (!miles || miles <= 0) return;

        if (!circleRef.current) {
            circleRef.current = new window.google.maps.Circle({
                map: googleMapRef.current,
                fillColor: '#534AB7', fillOpacity: 0.15, strokeColor: '#534AB7', strokeWeight: 2,
            });
        }
        circleRef.current.setCenter({ lat: radiusLat, lng: radiusLng });
        circleRef.current.setRadius(miles * 1609.34);
        googleMapRef.current.fitBounds(circleRef.current.getBounds());
    }, [mode, radiusLat, radiusLng, radiusMiles]);

    const confirmRadius = async () => {
        const miles = parseFloat(radiusMiles);
        if (radiusLat == null || radiusLng == null) { setError('Pick an address first.'); return; }
        if (!miles || miles <= 0) { setError('Enter a radius greater than 0.'); return; }
        setSaving(true);
        setError('');
        try {
            await api.addServiceAreaRule('Radius', ruleMode, undefined, undefined, undefined,
                radiusLat, radiusLng, miles, radiusAddress);
            resetRadius();
            onRuleAdded();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const resetRadius = () => {
        setRadiusAddress('');
        setRadiusLat(null);
        setRadiusLng(null);
        setRadiusMiles('25');
        clearOverlays();
    };

    return (
        <Card style={{ marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>🗺️ Map-assisted rule creation</h3>
            <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 16px' }}>
                Search a county for an approximate preview, or an address + radius for a precise circle, then save as a coverage rule.
            </p>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <Btn size="sm" variant={mode === 'county' ? 'primary' : 'outline'} onClick={() => switchMode('county')}>By county</Btn>
                <Btn size="sm" variant={mode === 'radius' ? 'primary' : 'outline'} onClick={() => switchMode('radius')}>By address + radius</Btn>
            </div>

            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <div ref={mapRef} style={{ width: '100%', height: 280, borderRadius: 8, marginBottom: 14, background: '#eee' }} />

            {mode === 'county' && (
                <div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 10 }}>
                        <div style={{ flex: 1 }}>
                            <Input label="County search" value={countyQuery} onChange={setCountyQuery} placeholder="Madison County, AL" />
                        </div>
                        <Btn size="sm" onClick={searchCounty} disabled={searching} style={{ marginBottom: 14 }}>
                            {searching ? 'Searching…' : 'Search'}
                        </Btn>
                    </div>
                    {countyResult && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 13, marginBottom: 14 }}>
                                <strong>{countyResult.county}, {countyResult.state}</strong>
                                <div style={{ color: C.textSub, fontSize: 12 }}>Approximate area shown — coverage is enforced by county name, not exact boundary.</div>
                            </div>
                            <div style={{ width: 190 }}>
                                <Select label="Mode" value={ruleMode} onChange={v => setRuleMode(v as RuleMode)}
                                    options={[{ value: 'Include', label: 'Include (expand coverage)' }, { value: 'Exclude', label: 'Exclude (limit coverage)' }]} />
                            </div>
                            <Btn size="sm" onClick={confirmCounty} disabled={saving} style={{ marginBottom: 14 }}>
                                {saving ? 'Saving…' : '+ Add rule'}
                            </Btn>
                        </div>
                    )}
                </div>
            )}

            {mode === 'radius' && (
                <div>
                    <AddressAutocomplete
                        label="Center address"
                        value={radiusAddress}
                        onChange={({ address, lat, lng }) => { setRadiusAddress(address); setRadiusLat(lat); setRadiusLng(lng); }}
                        placeholder="123 Main St, Huntsville, AL"
                    />
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div style={{ width: 140 }}>
                            <Input label="Radius (miles)" value={radiusMiles} onChange={setRadiusMiles} type="number" />
                        </div>
                        <div style={{ width: 190 }}>
                            <Select label="Mode" value={ruleMode} onChange={v => setRuleMode(v as RuleMode)}
                                options={[{ value: 'Include', label: 'Include (expand coverage)' }, { value: 'Exclude', label: 'Exclude (limit coverage)' }]} />
                        </div>
                        <Btn size="sm" onClick={confirmRadius} disabled={saving || radiusLat == null} style={{ marginBottom: 14 }}>
                            {saving ? 'Saving…' : '+ Add rule'}
                        </Btn>
                    </div>
                </div>
            )}
        </Card>
    );
}
